import React, { useState, useEffect } from "react";
import { X, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { isOptionVisible } from "../../utils/conditionalLogic";
import MeasurementPanel from "./config-sidebar/MeasurementPanel";
import StrapOptionPanel from "./config-sidebar/StrapOptionPanel";
import "../../styles/config-sidebar.css";

const WIDTH_OPTIONS  = Array.from({ length: 13 }, (_, i) => 14 + i);
const BUCKLE_OPTIONS = Array.from({ length: 17 }, (_, i) => 10 + i);
const SHORT_OPTIONS  = Array.from({ length: 41 }, (_, i) => 60 + i);
const LONG_OPTIONS   = Array.from({ length: 51 }, (_, i) => 100 + i);

const SIZE_FIELDS = [
    { key: 'width',       labelKey: 'lug_width_label',    labelDefault: 'Lug Width',    unit: 'mm', options: WIDTH_OPTIONS,   min: 14, max: 26  },
    { key: 'buckleWidth', labelKey: 'buckle_width_label', labelDefault: 'Buckle Width', unit: 'mm', options: BUCKLE_OPTIONS,  min: 10, max: 26  },
    { key: 'shortLength', labelKey: 'short_length_label', labelDefault: 'Short Length', unit: 'mm', options: SHORT_OPTIONS,   min: 60, max: 100 },
    { key: 'longLength',  labelKey: 'long_length_label',  labelDefault: 'Long Length',  unit: 'mm', options: LONG_OPTIONS,    min: 100, max: 150 },
];

const ConfigSidebar = ({
    isConfigOpen, setIsConfigOpen,
    config, onConfigChange, isOrderFinished,
    strapOptions, strapSelections, onStrapChange
}) => {
    const { t } = useTranslation();
    const [activeTab, setActiveTab]           = useState(null);
    const [tempStrapValue, setTempStrapValue] = useState(null);
    const [activeSubTab, setActiveSubTab]     = useState(null);
    const [showDescModal, setShowDescModal]   = useState(false);
    const [measInputs, setMeasInputs]   = useState({});
    const [measErrors, setMeasErrors]   = useState({});

    const activeStrapOption = (strapOptions || []).find(o => o.key === activeTab);
    const activeStrapOpts   = (strapOptions || [])
        .filter(o => o.isActive)
        .sort((a, b) => a.order - b.order);

    const visibleStrapOpts = activeStrapOpts.filter(o => isOptionVisible(o, strapSelections || {}));

    useEffect(() => {
        if (activeTab && activeTab !== 'measurements') {
            const stillVisible = visibleStrapOpts.some(o => o.key === activeTab);
            if (!stillVisible) {
                setActiveTab(null);
                setTempStrapValue(null);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [strapSelections]);

    const handleTabClick = (tabKey) => {
        if (isOrderFinished) return;
        if (activeTab === tabKey) {
            setActiveTab(null);
            setTempStrapValue(null);
        } else {
            setActiveTab(tabKey);
            if (tabKey === 'measurements') {
                const init = {};
                SIZE_FIELDS.forEach(f => { init[f.key] = String(config[f.key] ?? f.options[0]); });
                setMeasInputs(init);
                setMeasErrors({});
            } else {
                const opt = (strapOptions || []).find(o => o.key === tabKey);
                setTempStrapValue(opt ? ((strapSelections || {})[tabKey] || null) : null);
                setActiveSubTab(opt && opt.useTabs && opt.tabs?.length > 0 ? opt.tabs[0] : null);
            }
        }
    };

    const handleBack = () => {
        setActiveTab(null);
        setActiveSubTab(null);
        setTempStrapValue(null);
        setMeasInputs({});
        setMeasErrors({});
    };

    const handleClose = () => {
        setActiveTab(null);
        setTempStrapValue(null);
        setMeasInputs({});
        setMeasErrors({});
        setIsConfigOpen(false);
    };

    const handleStrapConfirm = () => {
        if (tempStrapValue !== null && activeStrapOption && onStrapChange) {
            const choices = activeStrapOption.choices || [];
            let rawVal = tempStrapValue;
            let customText = '';
            if (typeof rawVal === 'string' && rawVal.includes('||')) {
                const sepIdx = rawVal.indexOf('||');
                customText = rawVal.slice(sepIdx + 2);
                rawVal = rawVal.slice(0, sepIdx);
            }
            const matched = choices.find((c, i) => (c.value || c._id || String(i)) === rawVal);
            const actualChoice = matched ? matched.value : rawVal;
            const actualValue = customText !== '' ? `${actualChoice}||${customText}` : actualChoice;
            onStrapChange(activeStrapOption.key, actualValue);
        }
        setActiveTab(null);
        setTempStrapValue(null);
    };

    const handleMeasConfirm = () => {
        const newErrors = {};
        let hasError = false;
        SIZE_FIELDS.forEach(f => {
            const raw = measInputs[f.key];
            const v = parseInt(raw, 10);
            if (raw === '' || isNaN(v)) { newErrors[f.key] = 'Please enter a number'; hasError = true; return; }
            if (v < f.min || v > f.max) { newErrors[f.key] = `Out of range ${f.min}–${f.max} mm`; hasError = true; }
        });
        setMeasErrors(newErrors);
        if (hasError) return;
        SIZE_FIELDS.forEach(f => {
            onConfigChange({ target: { name: f.key, value: parseInt(measInputs[f.key], 10) } });
        });
        setActiveTab(null);
        setMeasInputs({});
        setMeasErrors({});
    };

    const handleMeasInputChange = (key, raw) => {
        const cleaned = raw.replace(/[^0-9]/g, '');
        setMeasInputs(prev => ({ ...prev, [key]: cleaned }));
        if (measErrors[key]) setMeasErrors(prev => ({ ...prev, [key]: null }));
    };

    const handleMeasBlur = (f) => {
        const raw = measInputs[f.key];
        if (raw === '' || isNaN(parseInt(raw, 10))) return;
        const v = parseInt(raw, 10);
        if (v < f.min || v > f.max) {
            setMeasErrors(prev => ({ ...prev, [f.key]: `Out of range ${f.min}–${f.max} mm` }));
        } else {
            setMeasErrors(prev => ({ ...prev, [f.key]: null }));
            onConfigChange({ target: { name: f.key, value: v } });
        }
    };

    const handleSliderChange = (fieldKey, val) => {
        const v = Number(val);
        onConfigChange({ target: { name: fieldKey, value: v } });
        setMeasInputs(prev => ({ ...prev, [fieldKey]: String(v) }));
        setMeasErrors(prev => ({ ...prev, [fieldKey]: null }));
    };

    const isPanelOpen = activeTab !== null;

    return (
        <div
            className="bespoke-sidebar-wrap"
            style={{ transform: `translateX(${isConfigOpen ? '0' : '-100%'})` }}
        >
            {/* ═══════════════ PRIMARY PANEL ═══════════════ */}
            <div className="cfg-primary">
                <div className="cfg-primary-header">
                    <h5 className="cfg-primary-title">Bespoke Options</h5>
                    <button className="cfg-close-btn" onClick={handleClose}>
                        <X size={14} color="#8B7D6B" />
                    </button>
                </div>

                <div style={{ overflowY: 'auto', flexGrow: 1, paddingTop: '4px' }}>
                    <button
                        className={`cfg-tab-btn${activeTab === 'measurements' ? ' active' : ''}`}
                        onClick={() => handleTabClick('measurements')}
                        disabled={isOrderFinished}
                    >
                        <div style={{ flexGrow: 1, minWidth: 0 }}>
                            <div className="cfg-tab-label">
                                {t('measurements', 'Measurements')}
                                <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>
                            </div>
                            <div className="cfg-tab-sub">
                                {config.width ?? '—'}mm · {config.buckleWidth ?? config.width ?? '—'}mm · {config.shortLength ?? '—'}mm · {config.longLength ?? '—'}mm
                            </div>
                        </div>
                        <ChevronRight size={14} className="cfg-chevron" />
                    </button>

                    {visibleStrapOpts.map(opt => {
                        const currentVal = (strapSelections || {})[opt.key];
                        let rawChoiceVal = currentVal;
                        let customInputText = '';
                        if (typeof currentVal === 'string' && currentVal.includes('||')) {
                            const sepIdx = currentVal.indexOf('||');
                            rawChoiceVal = currentVal.slice(0, sepIdx);
                            customInputText = currentVal.slice(sepIdx + 2);
                        }
                        const currentChoice = (opt.choices || []).find(c => c.value === rawChoiceVal || c._id === rawChoiceVal);
                        let displaySub = 'Not selected';
                        if (currentChoice) {
                            displaySub = customInputText ? `${currentChoice.label}: ${customInputText}` : currentChoice.label;
                        } else if (rawChoiceVal === 'on-tone') {
                            displaySub = 'On Tone';
                        } else if (rawChoiceVal && typeof rawChoiceVal === 'string' && rawChoiceVal.includes('[Addon]')) {
                            const parts = rawChoiceVal.split('|');
                            const textPart = parts[0].trim();
                            const addonPart = parts.length > 1 ? parts[1].trim() : '';
                            if (textPart && addonPart) { displaySub = `${textPart} | ${opt.addonLabel || 'No Logo'}`; }
                            else if (addonPart) { displaySub = opt.addonLabel || 'No Logo'; }
                            else { displaySub = textPart || 'Not selected'; }
                        } else if (rawChoiceVal) {
                            displaySub = rawChoiceVal;
                        }
                        return (
                            <button
                                key={opt.key}
                                className={`cfg-tab-btn${activeTab === opt.key ? ' active' : ''}`}
                                onClick={() => handleTabClick(opt.key)}
                                disabled={isOrderFinished}
                            >
                                <div style={{ flexGrow: 1, minWidth: 0 }}>
                                    <div className="cfg-tab-label">
                                        {opt.label}
                                        {(opt.isRequired || opt.required) && <span style={{ color: '#ef4444', marginLeft: '3px' }}>*</span>}
                                    </div>
                                    <div className={`cfg-tab-sub${currentVal ? ' selected-val' : ''}`}>
                                        {displaySub}
                                    </div>
                                </div>
                                <ChevronRight size={14} className="cfg-chevron" />
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* ═══════════════ SECONDARY PANEL ═══════════════ */}
            <div className={`cfg-secondary${isPanelOpen ? ' open' : ''}`}>
                <div className="cfg-secondary-inner">
                    {activeTab === 'measurements' && (
                        <MeasurementPanel
                            config={config}
                            measInputs={measInputs}
                            measErrors={measErrors}
                            isOrderFinished={isOrderFinished}
                            onInputChange={handleMeasInputChange}
                            onBlur={handleMeasBlur}
                            onSliderChange={handleSliderChange}
                            onBack={handleBack}
                            onConfirm={handleMeasConfirm}
                        />
                    )}
                    {activeTab && activeTab !== 'measurements' && (
                        <StrapOptionPanel
                            activeStrapOption={activeStrapOption}
                            tempStrapValue={tempStrapValue}
                            setTempStrapValue={setTempStrapValue}
                            activeSubTab={activeSubTab}
                            setActiveSubTab={setActiveSubTab}
                            isOrderFinished={isOrderFinished}
                            showDescModal={showDescModal}
                            setShowDescModal={setShowDescModal}
                            onBack={handleBack}
                            onConfirm={handleStrapConfirm}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfigSidebar;
